import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { AIAnalysisClient } from '../../../client/ai-analysis.client';
import {
  AIAnalysisRequest,
  AIAnalysisResult,
  AIAnalysisReview,
  TimelineEvent,
} from '../../entity/ai-analysis.model';

@Component({
  selector: 'ala-drawer',
  templateUrl: './ala-drawer.component.html',
  styleUrls: ['./ala-drawer.component.scss'],
})
export class AlaDrawerComponent {
  @Input() employeeName: string = '';
  @Input() subtitle: string = '';
  @Input() amountLabel: string = 'Amount Loaded';

  @Output() closed = new EventEmitter<void>();

  @ViewChild('ledgerPane') ledgerPane: ElementRef;
  @ViewChild('tblWrap') tblWrap: ElementRef;

  isOpen = false;
  isLoading = false;
  hasError = false;
  errorMessage = '';

  response: AIAnalysisResult = null;
  answerHtml: SafeHtml = '';
  amount: number = 0;
  isTimelineExpanded = false;
  stars = 0;

  // Summary pill values
  // TEMP: summary-bar-only fields commented out
  // totalCredited = 0;
  // totalLoadedToCard = 0;
  // reversals = 0;
  // cardLoadCount = 0;
  // hasReversals = false;
  ledgerEventCount = 0;

  // Badge mapping
  private badgeMap: Record<string, string> = {
    SYNC: 'ala-badge-sync',
    CLOSE_DATE: 'ala-badge-close',
    OPEN_DATE: 'ala-badge-open',
    LoadCard: 'ala-badge-load',
    Calculation: 'ala-badge-calc',
    'Calc Change': 'ala-badge-calc',
    ManualAdjustments: 'ala-badge-adj',
    'POS Change': 'ala-badge-pos',
    'Running Balance': 'ala-badge-rb',
    WIZARD_TIME_PROVIDER: 'ala-badge-sync',
  };

  private currentRequest: AIAnalysisRequest = null;

  constructor(
    private aiClient: AIAnalysisClient,
    private sanitizer: DomSanitizer
  ) {
    const renderer = new marked.Renderer();
    const defaultLinkRenderer = renderer.link.bind(renderer);

    renderer.link = (href: string, title: string, text: string) => {
      if (href && href.startsWith('row:')) {
        const rows = href.substring(4);
        return `<a class="ala-row-ref" data-rows="${rows}">${text} \u2193</a>`;
      }
      return defaultLinkRenderer(href, title, text);
    };

    marked.setOptions({ breaks: true, gfm: true, renderer });
  }

  open(request: AIAnalysisRequest) {
    this.currentRequest = request;
    this.amount = request.Amount;
    this.isOpen = true;
    this.isLoading = true;
    this.hasError = false;
    this.response = null;
    this.answerHtml = '';
    this.isTimelineExpanded = false;
    this.stars = 0;
    this.resetSummary();

    this.aiClient
      .analyzeAmount(request, 'Analyzing amount...')
      .then((data) => {
        this.response = data.result;
        this.answerHtml = this.sanitizer.bypassSecurityTrustHtml(
          marked.parse(this.response.Answer || '') as string
        );
        this.computeSummary(this.response.Timeline || []);
        this.isLoading = false;
      })
      .catch((err) => {
        this.hasError = true;
        this.errorMessage = err?.error?.Message || err?.message || 'An error occurred';
        this.isLoading = false;
      });
  }

  close() {
    this.isOpen = false;
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscPress() {
    if (this.isOpen) this.close();
  }

  onNarrativeClick(event: MouseEvent) {
    const anchor = (event.target as HTMLElement).closest('.ala-row-ref');
    if (!anchor) return;
    event.preventDefault();
    const rowsAttr = (anchor as HTMLElement).getAttribute('data-rows');
    if (!rowsAttr) return;
    const rows = rowsAttr.split(',').map(n => parseInt(n, 10)).filter(n => !isNaN(n));
    if (rows.length) this.scrollToRow(rows);
  }

  toggleTimeline() {
    this.isTimelineExpanded = !this.isTimelineExpanded;
  }

  get timelineToggleLabel(): string {
    return this.isTimelineExpanded ? 'Click to collapse' : 'Click to expand';
  }

  getBadgeClass(action: string): string {
    return this.badgeMap[action] || 'ala-badge-sync';
  }

  isRunningBalance(event: TimelineEvent): boolean {
    return event.Action === 'Running Balance';
  }

  getRowClass(event: TimelineEvent, index: number): string {
    if (this.isRunningBalance(event)) return 'rb';
    return index % 2 === 0 ? 'even' : '';
  }

  getCreditClass(event: TimelineEvent): string {
    if (!this.isRunningBalance(event)) return 'num';
    if (event.Credit != null && event.Credit < 0) return 'num credit-negative';
    if (event.Credit != null && event.Credit !== 0) return 'num credit-positive';
    return 'num';
  }

  formatSummary(value: string | null | undefined): string {
    if (!value) return '';
    try {
      const parsed = JSON.parse(value);
      return this.formatValue(parsed);
    } catch {
      return value;
    }
  }

  private formatValue(val: unknown): string {
    if (val == null) return '';
    if (typeof val !== 'object') return String(val);
    if (Array.isArray(val)) {
      return val.map(item => this.formatValue(item)).join(' | ');
    }
    return Object.entries(val)
      .map(([k, v]) => {
        if (v == null) return `${k}: N/A`;
        if (typeof v !== 'object') return `${k}: ${v}`;
        if (Array.isArray(v)) {
          const items = v.map(item => this.formatValue(item));
          return `${k}: [${items.join('; ')}]`;
        }
        return `${k}: {${this.formatValue(v)}}`;
      })
      .join(', ');
  }

  formatCurrency(value: number | null | undefined): string {
    if (value == null) return '';
    if (value === 0) return '$0.00';
    const neg = value < 0;
    const abs = Math.abs(value).toFixed(2);
    return neg ? '-$' + abs : '$' + abs;
  }

  submitReview() {
    if (!this.stars || !this.response || !this.currentRequest) return;

    const review: AIAnalysisReview = {
      RequestId: this.response.RequestId,
      CustomerCode: this.currentRequest.CustomerCode,
      EmployeeId: this.currentRequest.EmployeeId,
      Date:
        this.currentRequest.BusinessDateTo || this.currentRequest.LoadDate || '',
      Amount: this.currentRequest.Amount,
      Origin: this.currentRequest.Origin,
      Question: '',
      Answer: this.response.Answer,
      Stars: this.stars,
    };

    this.aiClient.submitReview(review);
  }

  setStars(value: number) {
    this.stars = value;
    this.submitReview();
  }

  private computeSummary(timeline: TimelineEvent[]) {
    // TEMP: summary-bar aggregations commented out
    // let credits = 0;
    // let debits = 0;
    // let adjustments = 0;
    // let loadCount = 0;
    //
    // timeline.forEach((r) => {
    //   if (r.Action === 'Running Balance') return;
    //   if (r.Credit != null && r.Credit !== 0) {
    //     if (r.Credit > 0) credits += r.Credit;
    //     if (r.Credit < 0) adjustments += Math.abs(r.Credit);
    //   }
    //   if (r.Debit != null && r.Debit > 0) {
    //     debits += r.Debit;
    //   }
    //   if (r.Action === 'LoadCard') loadCount++;
    // });
    //
    // this.totalCredited = credits;
    // this.totalLoadedToCard = debits;
    // this.reversals = adjustments;
    // this.hasReversals = adjustments > 0;
    // this.cardLoadCount = loadCount;
    this.ledgerEventCount = timeline.length;
  }

  private resetSummary() {
    // TEMP: summary-bar resets commented out
    // this.totalCredited = 0;
    // this.totalLoadedToCard = 0;
    // this.reversals = 0;
    // this.hasReversals = false;
    // this.cardLoadCount = 0;
    this.ledgerEventCount = 0;
  }

  private scrollToRow(rows: number[]) {
    const wasExpanded = this.isTimelineExpanded;
    if (!wasExpanded) {
      this.isTimelineExpanded = true;
    }
    setTimeout(() => {
      const el = document.getElementById('ala-row-' + rows[0]);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      rows.forEach(rowNo => {
        const rowEl = document.getElementById('ala-row-' + rowNo);
        if (rowEl) {
          rowEl.classList.add('ala-row-highlight');
          setTimeout(() => rowEl.classList.remove('ala-row-highlight'), 1800);
        }
      });
    }, wasExpanded ? 0 : 220);
  }
}
